import { getDatabase, ref, get, set, remove, push } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { NoteItem } from '../types/notes';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { recordActivity } from './activityApi';

export class NotesApi {
    private db = getDatabase();

    /**
     * Get notes path for a user
     */
    private getNotesPath(userId: string): string {
        return `users/notes/${userId}`;
    }

    /**
     * Validate and parse a note from Firebase data
     */
    private parseNote(data: unknown, key: string): NoteItem | null {
        if (!data || typeof data !== 'object') return null;
        const d = data as Record<string, unknown>;

        if (typeof d.text !== 'string' || typeof d.createdBy !== 'string' || typeof d.createdAt !== 'string') {
            console.warn(`Invalid note data at ${key}: missing required fields`);
            return null;
        }

        return {
            key,
            text: d.text,
            createdBy: d.createdBy,
            createdAt: d.createdAt,
            updatedAt: typeof d.updatedAt === 'string' ? d.updatedAt : undefined,
        };
    }

    /**
     * Fetch all notes for a user
     */
    async getNotes(userId: string): Promise<{ [key: string]: NoteItem }> {
        const user = getAuth().currentUser;
        if (!user || user.uid !== userId) {
            throw new Error('Du kan bara se dina egna anteckningar');
        }

        const notesPath = this.getNotesPath(userId);
        const notesRef = ref(this.db, notesPath);
        const snapshot = await get(notesRef);

        if (!snapshot.exists()) return {};

        const rawData = snapshot.val() as Record<string, unknown>;
        const validNotes: { [key: string]: NoteItem } = {};

        for (const [key, value] of Object.entries(rawData)) {
            const parsed = this.parseNote(value, key);
            if (parsed) {
                validNotes[key] = parsed;
            }
        }

        return validNotes;
    }

    /**
     * Create a new note
     */
    async createNote(userId: string, text: string): Promise<NoteItem> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad för att skapa en anteckning');
        }
        if (user.uid !== userId) {
            throw new Error('Du kan bara skapa anteckningar för dig själv');
        }

        if (!text.trim()) {
            throw new Error('Text krävs');
        }

        const notesPath = this.getNotesPath(userId);
        const notesRef = ref(this.db, notesPath);
        const newNoteRef = push(notesRef);

        const noteItem: NoteItem = {
            key: newNoteRef.key!,
            text: text.trim(),
            createdBy: user.uid,
            createdAt: formatDateForStorage(new Date()),
        };

        await set(newNoteRef, noteItem);
        recordActivity('note.created', { targetKey: noteItem.key });
        return noteItem;
    }

    /**
     * Update note text
     */
    async updateNote(userId: string, noteKey: string, currentNote: NoteItem, newText: string): Promise<void> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad för att uppdatera en anteckning');
        }
        if (user.uid !== userId) {
            throw new Error('Du kan bara uppdatera dina egna anteckningar');
        }

        if (!newText.trim()) {
            throw new Error('Text krävs');
        }

        const notesPath = this.getNotesPath(userId);
        const noteRef = ref(this.db, `${notesPath}/${noteKey}`);

        const updatedNote: NoteItem = {
            ...currentNote,
            text: newText.trim(),
            updatedAt: formatDateForStorage(new Date()),
        };

        await set(noteRef, updatedNote);
        recordActivity('note.updated', { targetKey: noteKey });
    }

    /**
     * Delete a note
     */
    async deleteNote(userId: string, noteKey: string): Promise<void> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad för att ta bort en anteckning');
        }
        if (user.uid !== userId) {
            throw new Error('Du kan bara ta bort dina egna anteckningar');
        }

        const notesPath = this.getNotesPath(userId);
        const noteRef = ref(this.db, `${notesPath}/${noteKey}`);
        await remove(noteRef);
        recordActivity('note.deleted', { targetKey: noteKey });
    }
}

// Export singleton instance
export const notesApi = new NotesApi();
